import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SBase } from '@setvi/shared/components';
import { FormikTextfield } from 'Components/Shared/FormikTextfield';
import { QuoteSectionDTO } from '@setvi/shared/services/react-query';

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  }
}));

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required('Section title is required'),
  description: Yup.string()
});

interface FormValues {
  name: string;
  description: string;
}

interface AddSectionDialogProps {
  onClose: () => void;
  addSection: (params: { name: string; description?: string }) => void;
}

interface EditSectionDialogProps {
  onClose: () => void;
  section: QuoteSectionDTO;
  updateSection: (params: {
    sectionId: string;
    name: string;
    description?: string;
  }) => void;
}

type SectionDialogProps = AddSectionDialogProps | EditSectionDialogProps;

const isEditMode = (
  props: SectionDialogProps
): props is EditSectionDialogProps =>
  'section' in props && 'updateSection' in props;

/**
 * Dialog for adding or editing a section in quote
 * Allows user to specify section name and description
 *
 * For create mode: pass `addSection` prop
 * For edit mode: pass `section` and `updateSection` props
 */
export const SectionDialog = (props: SectionDialogProps) => {
  const classes = useStyles();
  const { onClose } = props;
  const editMode = isEditMode(props);

  const initialValues: FormValues = editMode
    ? {
        name: props.section.name || '',
        description: props.section.note || ''
      }
    : {
        name: '',
        description: ''
      };

  const handleSubmit = (values: FormValues) => {
    if (editMode) {
      const sectionId = props.section.id || props.section._id;
      props.updateSection({
        sectionId,
        name: values.name.trim(),
        description: values.description.trim() || undefined
      });
    } else {
      props.addSection({
        name: values.name.trim(),
        description: values.description.trim() || undefined
      });
    }
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize>
      {({ isValid, dirty, submitForm }) => (
        <SBase
          header={{
            title: editMode ? 'Edit Section' : 'New Section',
            onClose
          }}
          footer={{
            onConfirm: submitForm,
            onClose,
            confirmLabel: editMode ? 'Save Changes' : 'Create Section',
            closeLabel: 'Cancel',
            // In edit mode, allow submit if valid (even without changes for consistency)
            // In create mode, require dirty state
            disableConfirm: !isValid || (!editMode && !dirty)
          }}
          width="592px"
          height="auto">
          <Form>
            <Box className={classes.content}>
              <FormikTextfield
                name="name"
                label="Section Title"
                placeholder="Add Section Title"
                required
                autoFocus
              />
              <FormikTextfield
                name="description"
                label="Description"
                placeholder="Add Description"
                multiline
                minRows={3}
              />
            </Box>
          </Form>
        </SBase>
      )}
    </Formik>
  );
};
